import { useMemo, useState } from "react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import {
  ArrowDownRight,
  ArrowUpRight,
  CalendarIcon,
  Eye,
  Globe,
  Minus,
  MousePointerClick,
  Smartphone,
  Users,
} from "lucide-react";
import type { DateRange as DayPickerRange } from "react-day-picker";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { WorldMap } from "@/components/admin/WorldMap";
import { useActiveVisitors, useAnalyticsSummary } from "@/hooks/usePageViews";
import { rangeFromDays, type DateRange, type RankedItem } from "@/lib/page-views";

type Preset = "7" | "30" | "90" | "365" | "custom";

const PRESETS: Array<{ value: Preset; label: string }> = [
  { value: "7", label: "Siste 7 dager" },
  { value: "30", label: "Siste 30 dager" },
  { value: "90", label: "Siste 90 dager" },
  { value: "365", label: "Siste 365 dager" },
  { value: "custom", label: "Egendefinert" },
];

function countryFlag(code: string): string {
  if (!code || code.length !== 2) return "🌍";
  const A = 0x1f1e6;
  const a = "A".charCodeAt(0);
  const upper = code.toUpperCase();
  return String.fromCodePoint(
    A + upper.charCodeAt(0) - a,
    A + upper.charCodeAt(1) - a
  );
}

function formatPct(n: number): string {
  return `${(n * 100).toFixed(1)}%`;
}

function formatTrend(n: number): string {
  const sign = n > 0 ? "+" : "";
  return `${sign}${(n * 100).toFixed(0)}%`;
}

const TrendBadge = ({
  value,
  invert = false,
}: {
  value: number;
  invert?: boolean;
}) => {
  if (!isFinite(value)) {
    return null;
  }
  const isUp = value > 0.001;
  const isDown = value < -0.001;
  const isPositive = invert ? isDown : isUp;
  const isNegative = invert ? isUp : isDown;
  const Icon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  const color = isPositive
    ? "text-primary"
    : isNegative
      ? "text-destructive"
      : "text-muted-foreground";
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${color}`}>
      <Icon className="h-3 w-3" />
      {formatTrend(value)}
    </span>
  );
};

const Kpi = ({
  label,
  value,
  trend,
  invertTrend = false,
  icon: Icon,
}: {
  label: string;
  value: string;
  trend?: number;
  invertTrend?: boolean;
  icon: React.ComponentType<{ className?: string }>;
}) => (
  <div className="rounded-lg border bg-card p-5">
    <div className="flex items-center justify-between">
      <p className="text-xs uppercase tracking-wider text-muted-foreground">{label}</p>
      <Icon className="h-4 w-4 text-primary" />
    </div>
    <div className="mt-2 flex items-baseline gap-2">
      <p className="text-2xl font-semibold">{value}</p>
      {trend !== undefined ? <TrendBadge value={trend} invert={invertTrend} /> : null}
    </div>
    <p className="mt-1 text-xs text-muted-foreground">vs forrige periode</p>
  </div>
);

const BarRow = ({
  label,
  count,
  total,
  prefix,
}: {
  label: string;
  count: number;
  total: number;
  prefix?: string;
}) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <li className="space-y-1">
      <div className="flex items-center justify-between gap-3 text-sm">
        <span className="flex min-w-0 items-center gap-2">
          {prefix ? <span>{prefix}</span> : null}
          <span className="truncate font-mono text-xs">{label}</span>
        </span>
        <span className="flex shrink-0 items-center gap-3 text-muted-foreground">
          <span>{count.toLocaleString("nb-NO")}</span>
          <span className="w-10 text-right text-xs">{Math.round(pct)}%</span>
        </span>
      </div>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted">
        <div
          className="h-full bg-primary/70"
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
    </li>
  );
};

const RankedList = ({
  rows,
  total,
  prefix,
  emptyLabel = "Ingen data ennå.",
}: {
  rows: RankedItem[];
  total: number;
  prefix?: (key: string) => string;
  emptyLabel?: string;
}) => {
  if (rows.length === 0) {
    return <p className="px-1 py-6 text-sm text-muted-foreground">{emptyLabel}</p>;
  }
  return (
    <ul className="space-y-3">
      {rows.map((row) => (
        <BarRow
          key={row.key}
          label={row.key}
          count={row.count}
          total={total}
          prefix={prefix?.(row.key)}
        />
      ))}
    </ul>
  );
};

const Card = ({
  title,
  children,
  className,
}: {
  title: string;
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={`rounded-lg border bg-card ${className ?? ""}`}>
    <div className="border-b px-5 py-3">
      <h3 className="text-sm font-medium">{title}</h3>
    </div>
    <div className="px-5 py-4">{children}</div>
  </div>
);

const RangePicker = ({
  preset,
  onPresetChange,
  customRange,
  onCustomChange,
}: {
  preset: Preset;
  onPresetChange: (p: Preset) => void;
  customRange: DateRange;
  onCustomChange: (r: DateRange) => void;
}) => {
  const [open, setOpen] = useState(false);
  const dayPickerRange: DayPickerRange = {
    from: customRange.from,
    to: customRange.to,
  };
  const label =
    preset === "custom"
      ? `${format(customRange.from, "d. MMM", { locale: nb })} – ${format(customRange.to, "d. MMM yyyy", { locale: nb })}`
      : "Velg datoer";
  return (
    <div className="flex items-center gap-2">
      <Select value={preset} onValueChange={(v) => onPresetChange(v as Preset)}>
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {preset === "custom" ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="font-normal">
              <CalendarIcon className="mr-2 h-4 w-4" />
              {label}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={dayPickerRange}
              defaultMonth={customRange.from}
              onSelect={(r) => {
                if (r?.from && r?.to) {
                  onCustomChange({ from: r.from, to: r.to });
                  setOpen(false);
                }
              }}
              numberOfMonths={2}
              locale={nb}
            />
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
};

const ActiveNowChip = () => {
  const { data } = useActiveVisitors();
  const sessions = data?.activeSessions ?? 0;
  const views = data?.activeViews ?? 0;
  return (
    <div className="mt-2 inline-flex items-center gap-2 rounded-full border bg-card px-3 py-1 text-xs text-muted-foreground">
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
      </span>
      <span>
        <span className="font-semibold text-foreground">
          {sessions.toLocaleString("nb-NO")}
        </span>{" "}
        aktive sesjoner siste 30 min
        {views > 0 ? (
          <span className="ml-1 text-muted-foreground/70">
            · {views.toLocaleString("nb-NO")} visninger
          </span>
        ) : null}
      </span>
    </div>
  );
};

const AdminAnalytics = () => {
  const [preset, setPreset] = useState<Preset>("30");
  const [customRange, setCustomRange] = useState<DateRange>(() => rangeFromDays(7));
  const [utmTab, setUtmTab] = useState<"source" | "medium" | "campaign">("source");

  const range = useMemo<DateRange>(
    () => (preset === "custom" ? customRange : rangeFromDays(Number(preset))),
    [preset, customRange]
  );

  const { data, isLoading, error } = useAnalyticsSummary(range);

  const chartTotal = data?.totalViews ?? 0;
  const countryDataMap = useMemo(() => {
    const m = new Map<string, number>();
    if (!data) return m;
    for (const row of data.topCountries) {
      if (row.key === "(ukjent)") continue;
      m.set(row.key, row.count);
    }
    return m;
  }, [data]);
  const showMap = countryDataMap.size > 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold">Analyse</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Sidevisninger, sesjoner og trafikk-kilder fra Vercel Web Analytics.
          </p>
          <ActiveNowChip />
        </div>
        <RangePicker
          preset={preset}
          onPresetChange={setPreset}
          customRange={customRange}
          onCustomChange={setCustomRange}
        />
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Laster …</p>
      ) : error ? (
        <p className="text-sm text-destructive">Kunne ikke laste analyse-data.</p>
      ) : !data ? null : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Kpi
              label="Sidevisninger"
              value={data.totalViews.toLocaleString("nb-NO")}
              trend={data.trend.totalViews}
              icon={Eye}
            />
            <Kpi
              label="Unike sesjoner"
              value={data.uniqueSessions.toLocaleString("nb-NO")}
              trend={data.trend.uniqueSessions}
              icon={Users}
            />
            <Kpi
              label="Bounce rate"
              value={formatPct(data.bounceRate)}
              trend={data.trend.bounceRate}
              invertTrend
              icon={MousePointerClick}
            />
          </div>

          <div className="rounded-lg border bg-card p-5">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-muted-foreground">
                Sidevisninger og sesjoner
              </h2>
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-primary" />
                  Visninger
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-2 w-2 rounded-full bg-nature-light" />
                  Sesjoner
                </span>
              </div>
            </div>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.daily}>
                  <defs>
                    <linearGradient id="viewsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="sessionsGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="hsl(var(--nature-light))" stopOpacity={0.35} />
                      <stop offset="100%" stopColor="hsl(var(--nature-light))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis
                    dataKey="day"
                    tickFormatter={(d: string) => d.slice(5)}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fontSize: 11 }}
                    stroke="hsl(var(--muted-foreground))"
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "hsl(var(--card))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelFormatter={(d: string) => d}
                  />
                  <Area
                    type="monotone"
                    dataKey="views"
                    name="Visninger"
                    stroke="hsl(var(--primary))"
                    fill="url(#viewsGradient)"
                    strokeWidth={2}
                  />
                  <Area
                    type="monotone"
                    dataKey="sessions"
                    name="Sesjoner"
                    stroke="hsl(var(--nature-light))"
                    fill="url(#sessionsGradient)"
                    strokeWidth={2}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <Tabs defaultValue="pages" className="w-full">
            <TabsList>
              <TabsTrigger value="pages">Sider</TabsTrigger>
              <TabsTrigger value="referrers">Referrers</TabsTrigger>
              <TabsTrigger value="utm">UTM</TabsTrigger>
            </TabsList>
            <TabsContent value="pages">
              <Card title="Topp sider">
                <RankedList rows={data.topPaths} total={chartTotal} />
              </Card>
            </TabsContent>
            <TabsContent value="referrers">
              <Card title="Topp referrers">
                <RankedList rows={data.topReferrers} total={chartTotal} />
              </Card>
            </TabsContent>
            <TabsContent value="utm">
              <Card title="UTM-parametre">
                <div className="mb-4 flex gap-2">
                  {(["source", "medium", "campaign"] as const).map((tab) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setUtmTab(tab)}
                      className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                        utmTab === tab
                          ? "bg-primary text-primary-foreground"
                          : "bg-muted text-muted-foreground hover:bg-muted/70"
                      }`}
                    >
                      utm_{tab}
                    </button>
                  ))}
                </div>
                <RankedList
                  rows={
                    utmTab === "source"
                      ? data.utmSources
                      : utmTab === "medium"
                        ? data.utmMediums
                        : data.utmCampaigns
                  }
                  total={chartTotal}
                  emptyLabel="Ingen UTM-tagget trafikk i perioden."
                />
              </Card>
            </TabsContent>
          </Tabs>

          <Card title="Land">
            <div className="flex items-center gap-2 pb-3 text-xs text-muted-foreground">
              <Globe className="h-3 w-3" />
              {data.topCountries.length} land i perioden
            </div>
            {showMap ? (
              <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
                <div className="rounded-md border bg-secondary/30 p-2">
                  <WorldMap data={countryDataMap} />
                </div>
                <RankedList
                  rows={data.topCountries}
                  total={chartTotal}
                  prefix={(key) => countryFlag(key)}
                />
              </div>
            ) : (
              <RankedList
                rows={data.topCountries}
                total={chartTotal}
                prefix={(key) => countryFlag(key)}
              />
            )}
          </Card>

          <div className="grid gap-4 lg:grid-cols-3">
            <Card title="Enheter">
              <div className="flex items-center gap-2 pb-3 text-xs text-muted-foreground">
                <Smartphone className="h-3 w-3" />
                Enhetstype
              </div>
              <RankedList rows={data.deviceMix} total={chartTotal} />
            </Card>
            <Card title="Nettlesere">
              <RankedList rows={data.browserMix} total={chartTotal} />
            </Card>
            <Card title="Operativsystem">
              <RankedList rows={data.osMix} total={chartTotal} />
            </Card>
          </div>
        </>
      )}
    </div>
  );
};

export default AdminAnalytics;
